import { Backend } from '../../../api/Backend';
import { ChannelModel } from '../../../api/EpgApi.types';
import { fullUrlWithRecipe, Recipes } from '../../utility/ImageRecipeUtil';
import { AssetType } from '../../../api/models/Shared.types';
import { IChannelLineupV5 } from '../../../api/models/AndromeTV.types';
import { EpgGuideProgramDataSource } from './EpgGuideProgramDataSource';
import { EpgUtil } from '../../utility/EpgUtil';
import { EpgApiV5 } from '../../../api/EpgApiV5';

const _constants = {
  dayDeviance: 7,
  oneDay: 24 * 60 * 60 * 1000, // hours*minutes*seconds*milliseconds
};

export class EpgGuideChannelDataSource {
  public readonly backend: Backend;
  private channelModel: ChannelModel;
  private channelNumber = -1;
  private channelImage: string | null = '';
  private _isFavorite = false;
  private schedulesCacheEachDay: EpgGuideProgramDataSource[][] = new Array(_constants.dayDeviance * 2).fill(undefined);
  private schedulesCacheEachDayPromises: Promise<void>[] = new Array(_constants.dayDeviance * 2).fill(undefined);
  private currentDay = new Date();

  constructor(backend: Backend, data: ChannelModel) {
    this.backend = backend;
    this.channelModel = data;
  }

  getImage(): string | null {
    if (this.channelImage === '') {
      if (this.channelModel.image.defaultLogo) {
        // logo options defaultLogo, posterLogo, squareLogo
        this.channelImage = fullUrlWithRecipe(this.backend, this.channelModel.image.defaultLogo, Recipes.Channel);
      } else {
        this.channelImage = null;
      }
    }
    return this.channelImage;
  }

  getChannelNumber(): number {
    if (this.channelNumber === -1) {
      if (this.backend.deviceProfile && this.channelModel.catalogs) {
        const currentCatalog = this.backend.deviceProfile.profile.catalog;
        const found = this.channelModel.catalogs.find(el => el.catalogId === currentCatalog);
        this.channelNumber = found ? found.channelNumber : -1;
      }
    }
    return this.channelNumber;
  }

  getChannelId(): string {
    return this.channelModel.channelId;
  }

  updateFavorite() {
    const cachedFavApi = this.backend.cachedFavoriteApi;
    return cachedFavApi
      .isFavorite(this.backend, { type: AssetType.CHANNEL, id: this.channelModel.channelId })
      .then(res => {
        this._isFavorite = res;
      });
  }

  isFavorite(): boolean {
    return this._isFavorite;
  }

  /**
   * return the list of programs from start to end
   * @param start
   * @param end
   */
  fetchPage(start: Date, end: Date) {
    return this.loadDay(start)
      .then(() => {
        return this.loadDay(end);
      })
      .then(() => {
        return this.findPrograms(start, end);
      });
  }

  findPrograms(start: Date, end: Date): EpgGuideProgramDataSource[] {
    let timeBlock: EpgGuideProgramDataSource[] = [];
    this.schedulesCacheEachDay.forEach(day => {
      // TODO limit search to 2 days
      if (!day) return;
      const filtered = day.filter(pr => {
        if (start.getTime() < pr.endTime && pr.endTime <= end.getTime()) return true;
        if (start.getTime() <= pr.startTime && pr.startTime < end.getTime()) return true;
        if (pr.startTime < start.getTime() && end.getTime() < pr.endTime) return true;
      });
      timeBlock = timeBlock.concat(filtered);
    });
    // remove duplicates
    return timeBlock.filter((v, i, a) => a.findIndex(v2 => v2.scheduleId === v.scheduleId) === i);
  }

  /**
   * load dayBlock and put into cache at correct spot
   */
  loadDay(day: Date): Promise<void> {
    const diffDays = this._differenceInDays(day);
    const dayLoadingPromise = this.schedulesCacheEachDayPromises[_constants.dayDeviance + diffDays];
    if (dayLoadingPromise) {
      // day already loaded
      return dayLoadingPromise;
    }
    const fullDay = EpgUtil.fromUntilFullDay(day);
    const dayProm = EpgApiV5.channelLineup(this.backend, this.getChannelId(), fullDay.from, fullDay.until).then(res => {
      this.schedulesCacheEachDay[_constants.dayDeviance + diffDays] = this._mapResultToGuideProgramDS(res);
    });
    this.schedulesCacheEachDayPromises[_constants.dayDeviance + diffDays] = dayProm;
    return dayProm;
  }

  _mapResultToGuideProgramDS(res: IChannelLineupV5): EpgGuideProgramDataSource[] {
    let result: EpgGuideProgramDataSource[] = [];
    if (res.schedules) {
      result = res.schedules.map(el => {
        const program = res.programs?.find(pr => pr.programId === el.programId);
        let seasonInfo = undefined;
        if (program) {
          seasonInfo = res.seasons?.find(se => se.seasonId === program.seasonId);
        }

        return new EpgGuideProgramDataSource(this.backend, el, this.channelModel, program, seasonInfo);
      });
    }

    function compareFn(a: EpgGuideProgramDataSource, b: EpgGuideProgramDataSource) {
      if (a.startTime < b.startTime) {
        return -1;
      }
      if (a.startTime > b.startTime) {
        return 1;
      }
      // a must be equal to b
      return 0;
    }

    result.sort(compareFn);
    return result;
  }

  _differenceInDays(day: Date): number {
    return Math.round((new Date(day).setHours(0, 0, 0, 0) - this.currentDay.setHours(0, 0, 0, 0)) / _constants.oneDay);
  }
}
